/**
 * Sales Calendar Service
 * CRUD for event assignments, rep availability, and calendar data aggregation
 */

import { supabase } from "./supabase";
import {
  changeEventStaffing,
  describeDateModelError,
  LEAD_REP_NOT_PERMITTED_MESSAGE,
  getEventStaffing,
  isDateModelActive,
  planLeadRep,
  type StaffingAssignment,
} from "./eventDateModel.service";

/** A date-model writer refusal as an Error whose message an operator can act on (original kept as cause). */
function friendlyDateModelError(e: unknown): Error {
  const friendly = describeDateModelError(e);
  if (!friendly) {
    return e instanceof Error
      ? e
      : Object.assign(
          new Error((e as { message?: string })?.message ?? "Save failed"),
          { cause: e },
        );
  }
  return Object.assign(new Error(friendly), { cause: e });
}
import type { UUID } from "../types/common.types";
import type {
  EventSalesAssignment,
  CreateEventSalesAssignmentInput,
  SalesRepAvailability,
  CreateAvailabilityInput,
  UpdateAvailabilityInput,
  CalendarEvent,
  CalendarEventType,
} from "../types/salesCalendar.types";
import { getRepColor, getEventTypeColor } from "../types/salesCalendar.types";
import { getEventDaysFor } from "./msrPortal.service";
import { getEventDayHours } from "./eventDayHours.service";
import {
  dailyHoursApplicability,
  resolveDailyHours,
  type DayHours,
} from "../utils/eventDayHours";
import {
  dayOfLabel,
  eventZone,
  expandEventDays,
  groupEventDays,
  localMidnightAfter,
  zonedLocalToInstant,
  type EventDayRow,
  type ScheduleInput,
  type ServiceDay,
} from "../utils/eventServiceDays";

/**
 * GS-03: one calendar entry per day an event runs (that day's hours), instead of one bar across the whole span
 * (which showed a Sat-Sun gun show only on Saturday in some views, and drew gap days for overnight events). Uses
 * event_days for date-model events, else the days derived from start/end in the event's time zone. `repDays` keeps
 * only the days a per-day rep works. An event with no plannable days keeps its single start..end entry.
 */
export function eventDaySegments(
  ev: ScheduleInput & { all_day?: boolean | null },
  modelDays: ServiceDay[] | undefined,
  repDays?: string[] | null,
  /** Hours set for each day (migration 2026-10-27-1120); a day with hours uses them instead of the uniform clock. */
  dayHours?: DayHours[] | null,
): {
  key: string;
  start: Date;
  end: Date;
  allDay: boolean;
  dayLabel: string;
}[] {
  const entries = expandEventDays(ev, modelDays, repDays);
  const resolved = ev.all_day ? null : resolveDailyHours(ev, dayHours);
  return entries.map((d) => {
    if (!d.date || !d.windowStart) {
      const start = new Date(ev.start_time ?? "");
      return {
        key: "all",
        start,
        end: ev.end_time ? new Date(ev.end_time) : start,
        allDay: !!ev.all_day,
        dayLabel: "",
      };
    }
    if (ev.all_day) {
      const [y, m, dd] = d.date.split("-").map(Number);
      return {
        key: d.date,
        start: new Date(y, m - 1, dd),
        end: new Date(y, m - 1, dd + 1),
        allDay: true,
        dayLabel: dayOfLabel(d),
      };
    }
    const hours = resolved?.find((h) => h.service_date === d.date);
    if (hours) {
      const tz = eventZone(ev.timezone);
      const secs = (t: string) => {
        const [hh, mm] = t.split(":").map(Number);
        return hh * 3600 + mm * 60;
      };
      return {
        key: d.date,
        start: new Date(zonedLocalToInstant(d.date, secs(hours.opens_at), tz)),
        end: new Date(zonedLocalToInstant(d.date, secs(hours.closes_at), tz)),
        allDay: false,
        dayLabel: dayOfLabel(d),
      };
    }
    return {
      key: d.date,
      start: new Date(d.windowStart),
      end: new Date(d.windowEnd ?? d.windowStart),
      allDay: false,
      dayLabel: dayOfLabel(d),
    };
  });
}

/** assignment id -> the YYYY-MM-DD days a per-day rep works (from event_sales_assignment_days + event_days). */
async function getSelectedRepDays(
  assignmentIds: UUID[],
  dayRows: EventDayRow[],
): Promise<Map<string, string[]>> {
  const out = new Map<string, string[]>();
  if (assignmentIds.length === 0) {
    return out;
  }
  const dateOf = new Map(
    dayRows.filter((d) => d.id).map((d) => [d.id as string, d.service_date]),
  );
  for (let i = 0; i < assignmentIds.length; i += 150) {
    const { data, error } = await supabase
      .from("event_sales_assignment_days")
      .select("assignment_id, event_day_id")
      .in("assignment_id", assignmentIds.slice(i, i + 150));
    if (error) {
      // Best effort: without the day list the rep's event shows on every day rather than vanishing.
      console.warn("getSelectedRepDays failed", error);
      return out;
    }
    for (const r of (data ?? []) as {
      assignment_id: string;
      event_day_id: string;
    }[]) {
      const date = dateOf.get(r.event_day_id);
      if (date) {
        out.set(r.assignment_id, [...(out.get(r.assignment_id) ?? []), date]);
      }
    }
  }
  return out;
}

// =====================================================
// EVENT SALES ASSIGNMENTS
// =====================================================

/** Does an event (start..end, ISO instants) run inside a YYYY-MM-DD range? One day of slack each side. */
export function eventOverlapsRange(
  start: string | null | undefined,
  end: string | null | undefined,
  dateRange: { start: string; end: string },
): boolean {
  const s = start ? Date.parse(start) : NaN;
  if (Number.isNaN(s)) {
    return false;
  }
  const e = end ? Date.parse(end) : NaN;
  const last = Number.isNaN(e) ? s : Math.max(s, e);
  const from = Date.parse(`${dateRange.start}T00:00:00Z`) - 86400000;
  const to = Date.parse(`${dateRange.end}T23:59:59Z`) + 86400000;
  return s <= to && last >= from;
}

export async function getAssignmentsForPositions(
  positionIds: UUID[],
  dateRange: { start: string; end: string },
): Promise<EventSalesAssignment[]> {
  if (positionIds.length === 0) {
    return [];
  }

  const { data, error } = await supabase
    .from("event_sales_assignments")
    .select(
      `*,
      event:events!event_sales_assignments_event_id_fkey(
        id, title, start_time, end_time, all_day, timezone, date_tbd,
        location_name, location_address,
        status, type, is_deleted
      ),
      sales_position:sales_positions!event_sales_assignments_sales_position_id_fkey(
        id, title, assigned_name, position_type
      )`,
    )
    .in("sales_position_id", positionIds);

  if (error) {
    throw error;
  }

  // Filter by date range client-side: any event that RUNS in the range (GS-03: a multi-day event that started
  // before the range still shows on its remaining days). One day of slack each side for time zones; the calendar
  // only draws what is visible.
  type Row = { event?: { start_time?: string; end_time?: string | null } };
  const filtered = ((data || []) as Row[]).filter((a) =>
    eventOverlapsRange(a.event?.start_time, a.event?.end_time, dateRange),
  );

  return filtered as EventSalesAssignment[];
}

export async function getEventSalesAssignments(
  eventId: UUID,
): Promise<EventSalesAssignment[]> {
  const { data, error } = await supabase
    .from("event_sales_assignments")
    .select(
      `*,
      sales_position:sales_positions!event_sales_assignments_sales_position_id_fkey(
        id, title, assigned_name, position_type
      )`,
    )
    .eq("event_id", eventId)
    .order("role")
    .order("created_at");

  if (error) {
    throw error;
  }
  return (data || []) as EventSalesAssignment[];
}

/**
 * The days each per-day ('selected') rep works on this event: assignment id -> sorted YYYY-MM-DD list.
 * All-days reps are absent from the map. Two plain reads (both tables are readable by any active CRM user).
 */
export async function getEventSalesAssignmentDays(
  eventId: UUID,
): Promise<Record<string, string[]>> {
  const [{ data: links, error: linkErr }, { data: days, error: dayErr }] =
    await Promise.all([
      supabase
        .from("event_sales_assignment_days")
        .select("assignment_id, event_day_id")
        .eq("event_id", eventId),
      supabase
        .from("event_days")
        .select("id, service_date")
        .eq("event_id", eventId),
    ]);
  if (linkErr) {
    throw linkErr;
  }
  if (dayErr) {
    throw dayErr;
  }
  const dateOf = new Map(
    (days ?? []).map((d: { id: string; service_date: string }) => [
      d.id,
      d.service_date,
    ]),
  );
  const out: Record<string, string[]> = {};
  for (const l of (links ?? []) as {
    assignment_id: string;
    event_day_id: string;
  }[]) {
    const date = dateOf.get(l.event_day_id);
    if (date) {
      (out[l.assignment_id] ??= []).push(date);
    }
  }
  for (const k of Object.keys(out)) {
    out[k].sort();
  }
  return out;
}

export interface LeadRepSyncResult {
  /** The Sales Reps list (and the event's Lead/Event Rep) was changed. */
  changed: boolean;
  /** Positions that were the lead before and now stay on the list as reps. */
  demoted: UUID[];
  /** Lead positions taken off the list because the operator chose "no rep" / self-service. */
  removed?: UUID[];
  /** Set when nothing was changed (the event's other fields may be saved). */
  error?: string;
}

/** Error text the database uses when the rep changed under the caller (compare-and-set, migration 417). */
const LEAD_REP_CHANGED = "LEAD_REP_CHANGED";

/**
 * Change the event's Lead/Event Rep AND the Lead on its Sales Reps list together (MSR activation fix 6; the list is
 * the source of truth). `next` = the rep to make Lead, or null for "no rep" / self-service, which takes the Lead off
 * the list (other reps stay). `expected` = the rep the caller's screen showed (compare-and-set): if someone else
 * changed it meanwhile, nothing is written and `error` says so ("reload").
 * Call it ONLY when the operator changed the rep (or to retry a failed sync of the same rep) - never on every save.
 * A non-model event: ONE set_event_lead_rep call (migration 417: one transaction, list + field). A date-model event:
 * the list through set_event_staffing (its version is the compare-and-set for the list; the field follows at commit,
 * server side), and a field with no Lead on the list is cleared through set_event_lead_rep. Never throws.
 */
export async function syncLeadRepToSalesReps(
  eventId: UUID,
  next: UUID | null | undefined,
  expected: UUID | null | undefined,
): Promise<LeadRepSyncResult> {
  const want = next ?? null;
  const seen = expected ?? null;
  const changedError = () =>
    Object.assign(new Error(`${LEAD_REP_CHANGED}: changed by someone else`), {
      code: "P0001",
    });
  try {
    if (await isDateModelActive(eventId)) {
      const cur = await getEventStaffing(eventId);
      const listLead =
        cur.assignments.find((a) => a.role === "lead")?.position_id ?? null;
      let field: UUID | null = null;
      if (listLead === null) {
        const { data, error } = await supabase
          .from("events")
          .select("sales_position_id")
          .eq("id", eventId)
          .single();
        if (error) {
          throw error;
        }
        field =
          (data as { sales_position_id?: UUID | null } | null)
            ?.sales_position_id ?? null;
      }
      const effective = listLead ?? field;
      if (effective !== seen && effective !== want) {
        throw changedError();
      }
      // Inside the writer's read-modify-write the list may have moved on (re-read after a version conflict).
      const guard = (list: StaffingAssignment[]) => {
        const l = list.find((a) => a.role === "lead")?.position_id ?? null;
        if (l !== listLead && l !== want) {
          throw changedError();
        }
      };
      if (want === null) {
        let removed: UUID[] = [];
        if (listLead !== null) {
          await changeEventStaffing(eventId, (list) => {
            guard(list);
            removed = list
              .filter((a) => a.role === "lead")
              .map((a) => a.position_id);
            return list.filter((a) => a.role !== "lead");
          });
        } else if (field !== null) {
          // nothing on the list; only the field to clear (allowed for a model event with no Lead on its list)
          const { error } = await supabase.rpc("set_event_lead_rep", {
            p_event_id: eventId,
            p_position_id: null,
            p_expected_position_id: field,
          });
          if (error) {
            throw error;
          }
        }
        return {
          changed: listLead !== null || field !== null,
          demoted: [],
          removed,
        };
      }
      if (planLeadRep(cur.assignments, want) === null) {
        return { changed: false, demoted: [], removed: [] };
      }
      let demoted: UUID[] = [];
      let changed = false;
      await changeEventStaffing(eventId, (list) => {
        guard(list);
        const plan = planLeadRep(list, want);
        if (!plan) {
          return list;
        }
        demoted = plan.demoted;
        changed = true;
        return plan.next;
      });
      return { changed, demoted, removed: [] };
    }
    // Any other event: one RPC (migration 417), one transaction, compare-and-set on the rep the screen showed.
    const { data, error } = await supabase.rpc("set_event_lead_rep", {
      p_event_id: eventId,
      p_position_id: want,
      p_expected_position_id: seen,
    });
    if (error) {
      throw error;
    }
    const res = (data ?? {}) as {
      changed?: boolean;
      demoted?: UUID[];
      removed?: UUID[];
    };
    return {
      changed: !!res.changed,
      demoted: res.demoted ?? [],
      removed: res.removed ?? [],
    };
  } catch (e) {
    return {
      changed: false,
      demoted: [],
      removed: [],
      error:
        describeDateModelError(e) ??
        (e as { message?: string } | null)?.message ??
        "unknown error",
    };
  }
}

/**
 * The toast (if any) that tells the operator what syncLeadRepToSalesReps did beyond the obvious.
 * `onCreate`: the call only puts the event's ALREADY SAVED Lead/Event Rep (events.sales_position_id, written by the
 * insert) on the new event's Sales Reps list. A refusal there leaves the field set, so "was not changed" would be
 * false: the toast says the event is saved with the rep, who is not on the Sales Reps list.
 */
export function leadRepSyncNotice(
  r: LeadRepSyncResult,
  opts?: { onCreate?: boolean },
): { type: "warning" | "info"; message: string } | null {
  if (r.error && opts?.onCreate) {
    return {
      type: "warning",
      message:
        r.error === LEAD_REP_NOT_PERMITTED_MESSAGE
          ? `The event is saved with this rep, but they were not added to its Sales Reps list: ${r.error.replace(/\.$/, "")}. Ask an admin to add them.`
          : `The event is saved with this rep, but they were not added to its Sales Reps list: ${r.error}`,
    };
  }
  if (r.error) {
    return {
      type: "warning",
      message: `The event is saved, but its Lead/Event Rep was not changed: ${r.error}`,
    };
  }
  if (r.demoted.length > 0) {
    return {
      type: "info",
      message:
        "The previous Lead rep stays on the Sales Reps list as a rep. Remove them on the Confirm Schedule tab if they are not working this event.",
    };
  }
  if ((r.removed ?? []).length > 0) {
    return {
      type: "info",
      message:
        "The Lead rep was taken off the event's Sales Reps list. Other reps on the list stay.",
    };
  }
  return null;
}

/**
 * The Confirm Schedule (Sales Reps) tab / Assign Rep dialog: make a position the Lead. Goes through syncLeadRepToSalesReps, so an
 * existing Lead is demoted to a rep in the same change (a direct insert hit the one-lead index, 23505).
 * `shownLead` = the Lead the list showed (null = none; then the event's field is the rep it is compared with).
 */
export async function makeEventSalesLead(
  eventId: UUID,
  positionId: UUID,
  shownLead: UUID | null,
): Promise<LeadRepSyncResult> {
  let expected = shownLead;
  if (expected === null) {
    const { data, error } = await supabase
      .from("events")
      .select("sales_position_id")
      .eq("id", eventId)
      .single();
    if (error) {
      throw error;
    }
    expected =
      (data as { sales_position_id?: UUID | null } | null)?.sales_position_id ??
      null;
  }
  const r = await syncLeadRepToSalesReps(eventId, positionId, expected);
  if (r.error) {
    throw new Error(r.error);
  }
  return r;
}

export async function createEventSalesAssignment(
  input: CreateEventSalesAssignmentInput,
  userId?: UUID,
): Promise<EventSalesAssignment> {
  // Date-model event: staffing changes only through set_event_staffing (the table refuses direct writes).
  if (await isDateModelActive(input.event_id)) {
    try {
      await changeEventStaffing(input.event_id, (cur) => [
        ...cur.filter((a) => a.position_id !== input.sales_position_id),
        {
          position_id: input.sales_position_id,
          role: input.role === "lead" ? "lead" : "assigned",
          day_scope: "all_days",
          days: [],
        },
      ]);
    } catch (e) {
      throw friendlyDateModelError(e);
    }
    const { data: row, error: readErr } = await supabase
      .from("event_sales_assignments")
      .select("*")
      .eq("event_id", input.event_id)
      .eq("sales_position_id", input.sales_position_id)
      .single();
    if (readErr) {
      throw readErr;
    }
    return row as EventSalesAssignment;
  }
  const { data, error } = await supabase
    .from("event_sales_assignments")
    .insert({ ...input, assigned_by: userId })
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as EventSalesAssignment;
}

export async function deleteEventSalesAssignment(id: UUID): Promise<void> {
  const { data: existing, error: findErr } = await supabase
    .from("event_sales_assignments")
    .select("event_id, sales_position_id")
    .eq("id", id)
    .maybeSingle();
  if (findErr) {
    throw findErr;
  }
  if (existing && (await isDateModelActive(existing.event_id as string))) {
    try {
      await changeEventStaffing(existing.event_id as string, (cur) =>
        cur.filter((a) => a.position_id !== existing.sales_position_id),
      );
    } catch (e) {
      throw friendlyDateModelError(e);
    }
    return;
  }
  const { error } = await supabase
    .from("event_sales_assignments")
    .delete()
    .eq("id", id);

  if (error) {
    // e.g. LEAD_REP_NEEDS_EVENT_EDIT (migration 417): removing the Lead needs edit rights on the event
    throw friendlyDateModelError(error);
  }
}

// =====================================================
// DIRECTLY ASSIGNED EVENTS (via events.sales_position_id)
// =====================================================

/**
 * Fetch events directly assigned to positions via events.sales_position_id.
 * These are events where the rep was set on the event form itself,
 * not via the event_sales_assignments join table.
 */
function shiftYmd(ymd: string, days: number): string {
  const t = Date.parse(`${ymd}T00:00:00Z`);
  return Number.isNaN(t)
    ? ymd
    : new Date(t + days * 86400000).toISOString().slice(0, 10);
}

async function getDirectlyAssignedEvents(
  positionIds: UUID[],
  dateRange: { start: string; end: string },
): Promise<any[]> {
  if (positionIds.length === 0) {
    return [];
  }

  const { data, error } = await supabase
    .from("events")
    .select(
      `id, title, start_time, end_time, all_day, timezone, date_tbd,
       location_name, location_address,
       status, type, is_deleted, sales_position_id,
       sales_position:sales_positions!events_sales_position_id_fkey(
         id, title, assigned_name, position_type
       )`,
    )
    .in("sales_position_id", positionIds)
    // GS-03: an event of up to 14 days that started before the range still runs in it.
    .gte("start_time", shiftYmd(dateRange.start, -15))
    .lte("start_time", dateRange.end + "T23:59:59")
    .or("is_deleted.is.null,is_deleted.eq.false");

  if (error) {
    console.error("Error fetching directly assigned events:", error);
    return [];
  }

  return (
    (data || []) as { start_time: string; end_time: string | null }[]
  ).filter((ev) => eventOverlapsRange(ev.start_time, ev.end_time, dateRange));
}

// =====================================================
// SALES REP AVAILABILITY
// =====================================================

export async function getAvailabilityForPositions(
  positionIds: UUID[],
  dateRange: { start: string; end: string },
): Promise<SalesRepAvailability[]> {
  if (positionIds.length === 0) {
    return [];
  }

  const { data, error } = await supabase
    .from("sales_rep_availability")
    .select(
      `*,
      sales_position:sales_positions!sales_rep_availability_sales_position_id_fkey(
        id, title, assigned_name
      )`,
    )
    .in("sales_position_id", positionIds)
    .lte("start_date", dateRange.end)
    .gte("end_date", dateRange.start)
    .order("start_date");

  if (error) {
    throw error;
  }
  return (data || []) as SalesRepAvailability[];
}

export async function createAvailability(
  input: CreateAvailabilityInput,
  userId?: UUID,
): Promise<SalesRepAvailability> {
  const { data, error } = await supabase
    .from("sales_rep_availability")
    .insert({ ...input, created_by: userId })
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as SalesRepAvailability;
}

export async function updateAvailability(
  id: UUID,
  updates: UpdateAvailabilityInput,
): Promise<SalesRepAvailability> {
  const { data, error } = await supabase
    .from("sales_rep_availability")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as SalesRepAvailability;
}

export async function deleteAvailability(id: UUID): Promise<void> {
  const { error } = await supabase
    .from("sales_rep_availability")
    .delete()
    .eq("id", id);

  if (error) {
    throw error;
  }
}

// =====================================================
// CALENDAR DATA AGGREGATION
// =====================================================

/**
 * Build a position-index color map from the list of position IDs.
 */
function buildColorMap(positionIds: UUID[]): Map<string, string> {
  const map = new Map<string, string>();
  positionIds.forEach((id, idx) => map.set(id, getRepColor(idx)));
  return map;
}

/**
 * Fetch calendar requests for the given positions/date range.
 * Calendar requests link to profiles via assigned_to, so we need to
 * cross-reference position.assigned_profile_id to find matching requests.
 */
async function getCalendarRequestsForPositions(
  positionIds: UUID[],
  dateRange: { start: string; end: string },
): Promise<any[]> {
  if (positionIds.length === 0) {
    return [];
  }

  // First get the profile IDs for these positions
  const { data: positions } = await supabase
    .from("sales_positions")
    .select("id, assigned_profile_id")
    .in("id", positionIds)
    .not("assigned_profile_id", "is", null);

  if (!positions || positions.length === 0) {
    return [];
  }

  const profileToPosition = new Map<string, string>();
  for (const p of positions) {
    if (p.assigned_profile_id) {
      profileToPosition.set(p.assigned_profile_id, p.id);
    }
  }

  const profileIds = Array.from(profileToPosition.keys());
  if (profileIds.length === 0) {
    return [];
  }

  const { data: requests, error } = await supabase
    .from("calendar_requests")
    .select(
      `*, organization:organizations(name),
       location:locations(name),
       assigned_profile:profiles!calendar_requests_assigned_to_fkey(id, full_name)`,
    )
    .in("assigned_to", profileIds)
    .gte("request_date", dateRange.start)
    .lte("request_date", dateRange.end)
    .not("status", "eq", "completed");

  if (error) {
    throw error;
  }

  // Attach position_id to each request
  return (requests || []).map((r: any) => ({
    ...r,
    _position_id: profileToPosition.get(r.assigned_to),
  }));
}

/**
 * Fetch all calendar data and merge into CalendarEvent[].
 */
export async function getCalendarData(
  positionIds: UUID[],
  dateRange: { start: string; end: string },
  options?: {
    showEvents?: boolean;
    showAvailability?: boolean;
    showCalendarRequests?: boolean;
  },
): Promise<CalendarEvent[]> {
  const {
    showEvents = true,
    showAvailability = true,
    showCalendarRequests = true,
  } = options || {};

  const colorMap = buildColorMap(positionIds);
  const events: CalendarEvent[] = [];

  // Parallel fetch
  const [assignments, availability, calRequests, directEvents] =
    await Promise.all([
      showEvents ? getAssignmentsForPositions(positionIds, dateRange) : [],
      showAvailability
        ? getAvailabilityForPositions(positionIds, dateRange)
        : [],
      showCalendarRequests
        ? getCalendarRequestsForPositions(positionIds, dateRange)
        : [],
      showEvents ? getDirectlyAssignedEvents(positionIds, dateRange) : [],
    ]);

  // GS-03: the days each event runs, and the days each per-day rep works.
  const dayEventIds = [
    ...assignments.map((a) => a.event?.id).filter((x): x is string => !!x),
    ...(directEvents as { id: string }[]).map((e) => e.id),
  ];
  const dayRows =
    dayEventIds.length > 0 ? await getEventDaysFor(dayEventIds) : [];
  const daysByEvent = groupEventDays(dayRows);
  // Hours each day (best effort: without them the chips keep the uniform daily clock).
  const hoursEventIds = [
    ...assignments
      .map((a) => a.event)
      .filter((e): e is NonNullable<typeof e> => !!e),
    ...(directEvents as unknown as (ScheduleInput & {
      id: string;
      all_day?: boolean | null;
    })[]),
  ]
    .filter(
      (e) =>
        dailyHoursApplicability(
          e as ScheduleInput & { all_day?: boolean | null },
        ).ok,
    )
    .map((e) => (e as { id: string }).id);
  let hoursByEvent = new Map<string, DayHours[]>();
  if (hoursEventIds.length > 0) {
    try {
      hoursByEvent = await getEventDayHours(hoursEventIds);
    } catch (e) {
      console.warn("getEventDayHours failed", e);
    }
  }
  const repDaysByAssignment = await getSelectedRepDays(
    assignments.filter((a) => a.day_scope === "selected").map((a) => a.id),
    dayRows,
  );

  // Track event IDs already added via join table to avoid duplicates
  const assignedEventIds = new Set<string>();

  // Map assignments to calendar events
  for (const a of assignments) {
    if (!a.event) {
      continue;
    }
    assignedEventIds.add(a.event.id);
    const repName =
      a.sales_position?.assigned_name || a.sales_position?.title || "Unknown";
    const eventColor = getEventTypeColor(a.event.type);
    const ev = a.event as typeof a.event & {
      timezone?: string | null;
      date_tbd?: boolean | null;
    };

    for (const seg of eventDaySegments(
      ev,
      daysByEvent.get(ev.id),
      repDaysByAssignment.get(a.id),
      hoursByEvent.get(ev.id),
    )) {
      events.push({
        id: seg.key === "all" ? a.id : `${a.id}:${seg.key}`,
        title: seg.dayLabel
          ? `${a.event.title} (${seg.dayLabel})`
          : a.event.title,
        start: seg.start,
        end: seg.end,
        allDay: seg.allDay,
        resource: {
          type: "event" as CalendarEventType,
          repName,
          repColor: eventColor,
          positionId: a.sales_position_id,
          originalData: a,
          eventStatus: a.event.status,
          eventType: a.event.type,
          locationName: a.event.location_name || undefined,
          locationAddress: (() => {
            const addr = a.event.location_address;
            if (!addr || typeof addr === "string") {
              return undefined;
            }
            const parts = [(addr as any).city, (addr as any).state].filter(
              Boolean,
            );
            return parts.length > 0 ? parts.join(", ") : undefined;
          })(),
        },
      });
    }
  }

  // Map directly assigned events (events.sales_position_id) not already in join table
  for (const ev of directEvents) {
    if (assignedEventIds.has(ev.id)) {
      continue;
    }
    const repName =
      ev.sales_position?.assigned_name || ev.sales_position?.title || "Unknown";
    const eventColor = getEventTypeColor(ev.type);
    const positionId = ev.sales_position_id!;

    for (const seg of eventDaySegments(
      ev,
      daysByEvent.get(ev.id),
      undefined,
      hoursByEvent.get(ev.id),
    )) {
      events.push({
        id:
          seg.key === "all" ? `direct-${ev.id}` : `direct-${ev.id}:${seg.key}`,
        title: seg.dayLabel ? `${ev.title} (${seg.dayLabel})` : ev.title,
        start: seg.start,
        end: seg.end,
        allDay: seg.allDay,
        resource: {
          type: "event" as CalendarEventType,
          repName,
          repColor: eventColor,
          positionId,
          originalData: ev,
          eventStatus: ev.status,
          eventType: ev.type,
          locationName: ev.location_name || undefined,
          locationAddress: (() => {
            const addr = ev.location_address;
            if (!addr || typeof addr === "string") {
              return undefined;
            }
            const parts = [(addr as any).city, (addr as any).state].filter(
              Boolean,
            );
            return parts.length > 0 ? parts.join(", ") : undefined;
          })(),
        },
      });
    }
  }

  // Map availability blocks to calendar events
  for (const av of availability) {
    const repName =
      av.sales_position?.assigned_name || av.sales_position?.title || "Unknown";
    const color = colorMap.get(av.sales_position_id) || "#6b7280";
    const typeLabel =
      (
        {
          pto: "PTO",
          unavailable: "Unavailable",
          training: "Training",
          travel: "Travel",
          out_of_town: "Out of Town",
        } as Record<string, string>
      )[av.availability_type] || av.availability_type;

    // SA-5: a timed (intra-day) block renders at its time on its single day;
    // an all-day block spans the inclusive date range as an all-day bar.
    const isTimed = av.is_all_day === false && !!av.start_time && !!av.end_time;
    events.push({
      id: av.id,
      title: av.title || typeLabel,
      start: isTimed
        ? new Date(av.start_date + "T" + av.start_time)
        : new Date(av.start_date + "T00:00:00"),
      // end_date is inclusive, add 1 day for all-day calendar rendering
      end: isTimed
        ? new Date(av.start_date + "T" + av.end_time)
        : localMidnightAfter(av.end_date),
      allDay: !isTimed,
      resource: {
        type: "availability" as CalendarEventType,
        repName,
        repColor: color,
        positionId: av.sales_position_id,
        originalData: av,
        availabilityType: av.availability_type,
      },
    });
  }

  // Map calendar requests to calendar events
  for (const cr of calRequests) {
    const positionId = cr._position_id;
    if (!positionId) {
      continue;
    }
    const color = colorMap.get(positionId) || "#6b7280";

    events.push({
      id: cr.id,
      title: cr.description || "Calendar Request",
      start: new Date(cr.request_date + "T" + (cr.request_time || "09:00:00")),
      end: new Date(cr.request_date + "T" + (cr.request_time || "10:00:00")),
      allDay: !cr.request_time,
      resource: {
        type: "calendar_request" as CalendarEventType,
        repName: cr.assigned_profile?.full_name || "Unknown",
        repColor: color,
        positionId,
        originalData: cr,
        locationName: cr.organization?.name || cr.location?.name || undefined,
      },
    });
  }

  return events;
}
